import { useEffect, useState } from "react";

type Facts = {
  health: "LOADING" | "LIVE" | "DEGRADED";
  mutationGate: "UNKNOWN" | "DISABLED" | "ENABLED";
  rowCount: number | null;
};

const INITIAL: Facts = {
  health: "LOADING",
  mutationGate: "UNKNOWN",
  rowCount: null,
};

export function useLiveFacts(): Facts {
  const [facts, setFacts] = useState<Facts>(INITIAL);

  useEffect(() => {
    const controller = new AbortController();
    Promise.all([
      fetch("/api/health", {
        headers: { Accept: "application/json" },
        signal: controller.signal,
      }).then(async (response) => {
        if (!response.ok) throw new Error(`health ${response.status}`);
        return (await response.json()) as Record<string, unknown>;
      }),
      fetch("/api/proof-window", {
        headers: { Accept: "application/json" },
        signal: controller.signal,
      }).then(async (response) => {
        if (!response.ok) throw new Error(`proof-window ${response.status}`);
        return (await response.json()) as Record<string, unknown>;
      }),
    ])
      .then(([health, proofWindow]) => {
        const gate = health.mutation_gate;
        const rows = proofWindow.rows;
        if (
          health.service !== "aevion-evidence-terminal" ||
          (gate !== "ENABLED" && gate !== "DISABLED") ||
          !Array.isArray(rows)
        ) {
          throw new Error("live facts schema mismatch");
        }
        setFacts({ health: "LIVE", mutationGate: gate, rowCount: rows.length });
      })
      .catch((error: unknown) => {
        if (error instanceof DOMException && error.name === "AbortError") return;
        setFacts({ health: "DEGRADED", mutationGate: "UNKNOWN", rowCount: null });
      });

    return () => controller.abort();
  }, []);

  return facts;
}

export function LiveSignalStrip() {
  const facts = useLiveFacts();
  const count = facts.rowCount === null ? "--" : String(facts.rowCount).padStart(2, "0");
  const ingress = facts.mutationGate === "ENABLED" ? "AUTH" : facts.mutationGate === "DISABLED" ? "HOLD" : "--";

  return (
    <section className="signal-strip" aria-label="Current deployment facts">
      <div><span>01</span><strong>LIVE WORKER</strong><small>Cloudflare edge</small></div>
      <div><span>01</span><strong>D1 STORE</strong><small>receipt projection</small></div>
      <div><span>{count}</span><strong>PUBLIC ROWS</strong><small>live D1 read model</small></div>
      <div className={facts.mutationGate === "ENABLED" ? "" : "signal-strip__hold"}>
        <span>{ingress}</span><strong>WRITE INGRESS</strong>
        <small>{facts.mutationGate === "ENABLED" ? "bearer token required" : "not accepting writes"}</small>
      </div>
    </section>
  );
}
